/**
 * Provides the Milan Progress primitive. It accepts React component props,
 * renders an accessible 0–100 progress element, and has no external side
 * effects beyond React rendering.
 *
 * @module components/ui/progress
 */
import React from 'react';
import { Progress as ProgressPrimitive } from 'radix-ui';

function clampProgress(value) {
  return Math.max(0, Math.min(100, Number(value) || 0));
}

/**
 * Renders an accessible Radix progress bar with a value clamped from 0 to 100.
 *
 * @param {object} props - Progress component props.
 * @param {number} [props.value=0] - Current progress value.
 * @param {string} props.label - Accessible progress-bar label.
 * @param {string} [props.className=''] - Additional root class names.
 * @returns {React.ReactElement} The rendered progress-bar primitive.
 * @sideEffects Renders React elements; it has no external side effects.
 */
export default function Progress({
  value = 0,
  label,
  current,
  total,
  className = '',
  ...props
}) {
  const safeValue = clampProgress(value);
  const indicatorStyle = {
    transform: 'translateX(-' + (100 - safeValue) + '%)',
  };

  const showStatus = typeof current === 'number' && typeof total === 'number';

  const content = (
    <ProgressPrimitive.Root
      {...props}
      aria-label={label}
      className={('milan-progress ' + className).trim()}
      value={safeValue}
      max={100}
    >
      <ProgressPrimitive.Indicator
        className="milan-progress__indicator"
        style={indicatorStyle}
      />
    </ProgressPrimitive.Root>
  );

  if (showStatus) {
    return (
      <div
        className="milan-progress-container"
        role="progressbar"
        aria-valuenow={safeValue}
        aria-valuemin="0"
        aria-valuemax="100"
        aria-label={label}
      >
        <div className="milan-progress-segments">
          {Array.from({ length: total }, (_, index) => {
            const isComplete = index < current;
            const isCurrent = index === current;
            let segmentClass = 'milan-progress-segment';
            if (isComplete) {
              segmentClass += ' milan-progress-segment--complete';
            } else if (isCurrent) {
              segmentClass += ' milan-progress-segment--current';
            } else {
              segmentClass += ' milan-progress-segment--future';
            }
            return (
              <div
                key={index}
                className={segmentClass}
              />
            );
          })}
        </div>
      </div>
    );
  }

  return content;
}
